import { useEffect, useMemo, useState } from "react";
import { Loader2, Tag, Plus, Edit3, Trash2, Save, X } from "lucide-react";
import { Pagination, pageSlice } from "../components/Pagination";
import {
  useConceptos,
  useCreateConcepto,
  useUpdateConcepto,
  useDeleteConcepto,
} from "../api/conceptos";
import { useIssuers } from "../api/issuers";
import { useClientes } from "../api/clientes";
import type { Concepto, ConceptoFormData } from "../types/concepto";
import { errMsg } from "@shared/api/errors";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";

const EMPTY_FORM: ConceptoFormData = {
  emisor_id: 0,
  cliente_id: null,
  descripcion: "",
  precio_unitario: null,
  iva: 21,
};

export function ConceptosListPage() {
  const [filterEmisor, setFilterEmisor] = useState<number | undefined>();
  const [filterCliente, setFilterCliente] = useState<number | undefined>();
  const { data: conceptos, isLoading, error } = useConceptos(filterEmisor, filterCliente);
  const { data: emisores } = useIssuers();
  const { data: clientes } = useClientes(filterEmisor);

  const createMut = useCreateConcepto();
  const updateMut = useUpdateConcepto();
  const deleteMut = useDeleteConcepto();

  const [editing, setEditing] = useState<Concepto | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<ConceptoFormData>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<Concepto | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => {
    setPage(1);
  }, [filterEmisor, filterCliente]);
  const pagedConceptos = useMemo(
    () => pageSlice(conceptos, page, pageSize),
    [conceptos, page, pageSize],
  );

  function openCreate() {
    setCreating(true);
    setEditing(null);
    setForm({ ...EMPTY_FORM, emisor_id: filterEmisor ?? emisores?.[0]?.id ?? 0 });
  }
  function openEdit(c: Concepto) {
    setEditing(c);
    setCreating(false);
    setForm({
      emisor_id: c.emisor_id,
      cliente_id: c.cliente_id,
      descripcion: c.descripcion,
      precio_unitario: c.precio_unitario,
      iva: c.iva,
    });
  }
  function close() {
    setEditing(null);
    setCreating(false);
    setFormError(null);
  }
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, payload: form });
      } else {
        await createMut.mutateAsync(form);
      }
      close();
    } catch (err) {
      setFormError(errMsg(err, "No se pudo guardar el concepto."));
    }
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <Tag size={28} className="text-brand-700" /> Conceptos
          </h1>
          <p className="text-slate-500 mt-1">
            Plantillas reutilizables de líneas de factura.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 font-bold text-sm shadow-soft transition"
        >
          <Plus size={18} /> Nuevo concepto
        </button>
      </header>

      <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-4 grid grid-cols-1 md:grid-cols-2 gap-3 shrink-0">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Emisor</label>
          <select
            value={filterEmisor ?? ""}
            onChange={(e) => {
              setFilterEmisor(e.target.value ? Number(e.target.value) : undefined);
              setFilterCliente(undefined);
            }}
            className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Todos</option>
            {emisores?.map((em) => (
              <option key={em.id} value={em.id}>
                {em.nombre}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Cliente</label>
          <select
            value={filterCliente ?? ""}
            onChange={(e) => setFilterCliente(e.target.value ? Number(e.target.value) : undefined)}
            className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Todos</option>
            {clientes?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-4">
          Error al cargar.
        </div>
      )}

      {conceptos && (
        <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden flex flex-col">
          <div className="overflow-auto max-h-[calc(100vh-300px)]">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Descripción</th>
                  <th className="text-right px-4 py-3 font-bold">Precio</th>
                  <th className="text-right px-4 py-3 font-bold">% IVA</th>
                  <th className="text-left px-4 py-3 font-bold">Cliente</th>
                  <th className="text-right px-4 py-3 font-bold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {conceptos.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                      No hay conceptos.
                    </td>
                  </tr>
                )}
                {pagedConceptos.map((c) => {
                const cli = clientes?.find((x) => x.id === c.cliente_id);
                return (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-bold text-slate-900">{c.descripcion}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">
                      {c.precio_unitario !== null
                        ? new Intl.NumberFormat("es-ES", {
                            style: "currency",
                            currency: "EUR",
                          }).format(c.precio_unitario)
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{c.iva} %</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {cli?.nombre ?? <span className="text-slate-400">— global —</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(c)}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={() => setConfirmDel(c)}
                          className="p-1.5 rounded hover:bg-rose-50 text-rose-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={conceptos.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      )}

      {(creating || editing) && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">
                {editing ? "Editar concepto" : "Nuevo concepto"}
              </h2>
              <button type="button" onClick={close} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>
            {formError && (
              <div className="mb-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-sm">
                {formError}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Emisor *
                </label>
                <select
                  required
                  value={form.emisor_id}
                  onChange={(e) =>
                    setForm({ ...form, emisor_id: Number(e.target.value), cliente_id: null })
                  }
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value={0}>— elegir —</option>
                  {emisores?.map((em) => (
                    <option key={em.id} value={em.id}>
                      {em.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Cliente (opcional, si es específico)
                </label>
                <select
                  value={form.cliente_id ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, cliente_id: e.target.value ? Number(e.target.value) : null })
                  }
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">— global del emisor —</option>
                  {clientes
                    ?.filter((c) => c.emisor_id === form.emisor_id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nombre}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Descripción *
                </label>
                <input
                  required
                  type="text"
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Precio (opcional)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.precio_unitario ?? ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        precio_unitario: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    % IVA
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.iva ?? 21}
                    onChange={(e) => setForm({ ...form, iva: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
              >
                <Save size={14} /> Guardar
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={confirmDel !== null}
        title="Eliminar concepto"
        message={confirmDel ? `¿Eliminar el concepto "${confirmDel.descripcion}"?` : ""}
        confirmLabel="Eliminar"
        destructive
        onConfirm={() => {
          if (confirmDel) deleteMut.mutate(confirmDel.id);
        }}
        onClose={() => setConfirmDel(null)}
      />
    </div>
  );
}
